import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import { REPORT_EARLYBIRD_QUERY } from '../gql';
import { ECountryCode, ReportEarlyBirdQuery, EOfferSource } from '__generated__/graphql';
import { allCountriesSorted } from 'languages';
import { formatNumber, formatPrice } from 'semshared/utils/format';
import { formatPercentage } from 'components/Percentage';
import { SimpleDatePicker } from 'components/form/FormInputDate';
import { FormatDateRange } from 'components/DateTime';
import { filter } from 'lodash';
import StatisticsBookingsModal, { ReportModalEntityType } from './components/StatisticsBookingsModal';
import { StatisticsReportBookingsFiltersInput } from '__generated__/graphql';

interface EarlyBirdFilters {
  country?: ECountryCode;
  date?: Date;
  premium?: boolean;
}

/**
 * Early bird report tab showing booking lead times.
 * Analyzes time between offer creation and booking confirmation.
 */
export default function EarlybirdTab() {
  const [filters, setFilters] = useState<EarlyBirdFilters>({});
  const [results, setResults] = useState<ReportEarlyBirdQuery['reportEarlyBird'] | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<EarlyBirdFilters | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFilters, setModalFilters] = useState<StatisticsReportBookingsFiltersInput | null>(null);
  const [modalColumnLabel, setModalColumnLabel] = useState('');
  const [modalEntityType, setModalEntityType] = useState<ReportModalEntityType>('booking');

  const [executeEarlyBirdQuery] = useLazyQuery(REPORT_EARLYBIRD_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleFilterChange = (field: keyof EarlyBirdFilters, value: any) => {
    setFilters((prev) => {
      if (value === undefined || value === null || value === '') {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      }
      return { ...prev, [field]: value };
    });
  };

  const handleReset = () => {
    setFilters({});
    setResults(null);
    setAppliedFilters(null);
  };

  const handleGenerateReport = async () => {
    if (!filters.date) {
      alert(String(i18next.t('earlybird-select-date')));
      return;
    }

    setLoading(true);
    setResults(null);
    setAppliedFilters(filters);

    try {
      const result = await executeEarlyBirdQuery({
        variables: {
          country: filters.country || undefined,
          date: filters.date || null,
          premium: filters.premium !== undefined ? filters.premium : undefined,
        },
      });

      setResults(result.data?.reportEarlyBird || null);
    } catch (error) {
      console.error('Early Bird query error:', error);
      alert(String(i18next.t('report-load-error')));
    } finally {
      setLoading(false);
    }
  };

  // CSV Export
  const handleExportCSV = () => {
    if (!results) {
      alert(String(i18next.t('report-no-export-data')));
      return;
    }

    const headers = [
      String(i18next.t('earlybird-metric')),
      String(i18next.t('earlybird-period-day')),
      String(i18next.t('earlybird-period-week')),
      String(i18next.t('earlybird-period-month')),
      String(i18next.t('earlybird-period-year')),
    ];
    const rows = [
      [String(i18next.t('contact-net-inquiries')),
        formatNumber(results.day.bookingCount),
        formatNumber(results.week.bookingCount),
        formatNumber(results.month.bookingCount),
        formatNumber(results.year.bookingCount)
      ],
      [String(i18next.t('contact-gross-inquiries')),
        formatNumber(results.day.offerCount),
        formatNumber(results.week.offerCount),
        formatNumber(results.month.offerCount),
        formatNumber(results.year.offerCount)
      ],
      [String(i18next.t('contact-reservations')),
        formatNumber(results.day.reservationCount),
        formatNumber(results.week.reservationCount),
        formatNumber(results.month.reservationCount),
        formatNumber(results.year.reservationCount)
      ],
      [String(i18next.t('earlybird-conversion-rate')),
        formatNumber(results.day.bookingCount > 0 ? (results.day.reservationCount / results.day.bookingCount) * 100 : 0, undefined, 2),
        formatNumber(results.week.bookingCount > 0 ? (results.week.reservationCount / results.week.bookingCount) * 100 : 0, undefined, 2),
        formatNumber(results.month.bookingCount > 0 ? (results.month.reservationCount / results.month.bookingCount) * 100 : 0, undefined, 2),
        formatNumber(results.year.bookingCount > 0 ? (results.year.reservationCount / results.year.bookingCount) * 100 : 0, undefined, 2),
      ],
      [String(i18next.t('earlybird-commission')),
        formatNumber(results.day.commissionAmountNet),
        formatNumber(results.week.commissionAmountNet),
        formatNumber(results.month.commissionAmountNet),
        formatNumber(results.year.commissionAmountNet)
      ],
      [String(i18next.t('earlybird-hotel-revenue')),
        formatNumber(results.day.totalAmountNet),
        formatNumber(results.week.totalAmountNet),
        formatNumber(results.month.totalAmountNet),
        formatNumber(results.year.totalAmountNet)
      ],
    ];

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell: any) => `"${String(cell)}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `earlybird-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tableRows = results
    ? [
      {
        label: String(i18next.t('contact-net-inquiries')),
        day: results.day.bookingCount,
        week: results.week.bookingCount,
        month: results.month.bookingCount,
        year: results.year.bookingCount,
        isCurrency: false,
      },
      {
        label: String(i18next.t('contact-gross-inquiries')),
        day: results.day.offerCount,
        week: results.week.offerCount,
        month: results.month.offerCount,
        year: results.year.offerCount,
        isCurrency: false,
      },
      {
        label: String(i18next.t('contact-reservations')),
        day: results.day.reservationCount,
        week: results.week.reservationCount,
        month: results.month.reservationCount,
        year: results.year.reservationCount,
        isCurrency: false,
      },
      {
        label: String(i18next.t('earlybird-conversion-rate')),
        day: results.day.bookingCount > 0 ? (results.day.reservationCount / results.day.bookingCount) * 100 : 0,
        week: results.week.bookingCount > 0 ? (results.week.reservationCount / results.week.bookingCount) * 100 : 0,
        month: results.month.bookingCount > 0 ? (results.month.reservationCount / results.month.bookingCount) * 100 : 0,
        year: results.year.bookingCount > 0 ? (results.year.reservationCount / results.year.bookingCount) * 100 : 0,
        isCurrency: false,
        isPercentage: true,
      },
      {
        label: String(i18next.t('earlybird-commission')),
        day: results.day.commissionAmountNet,
        week: results.week.commissionAmountNet,
        month: results.month.commissionAmountNet,
        year: results.year.commissionAmountNet,
        isCurrency: true,
      },
      {
        label: String(i18next.t('earlybird-hotel-revenue')),
        day: results.day.totalAmountNet,
        week: results.week.totalAmountNet,
        month: results.month.totalAmountNet,
        year: results.year.totalAmountNet,
        isCurrency: true,
      },
    ]
    : [];

  const selectedCountry = appliedFilters?.country
    ? allCountriesSorted.find((country) => country?.code === appliedFilters.country)
    : null;

  const countryLabel = selectedCountry?.label || String(i18next.t('filter-status-all'));

  const customerTypeLabel =
    appliedFilters?.premium === undefined
      ? String(i18next.t('filter-status-all'))
      : appliedFilters.premium
        ? String(i18next.t('filter-customer-type-premium'))
        : String(i18next.t('filter-customer-type-classic'));

  return (
    <Box component="form" sx={{ width: '100%' }}>
      {/* Filters */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, mb: 4 }}>
        {/* Country Filter */}
        <FormControl>
          <InputLabel shrink>{i18next.t('filter-country')}</InputLabel>
          <Select
            label={String(i18next.t('filter-country'))}
            value={filters.country || ''}
            onChange={(e) => handleFilterChange('country', (e.target.value as ECountryCode) || undefined)}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {allCountriesSorted.map(
              (country) =>
                country && (
                  <MenuItem key={country.code} value={country.code}>
                    {country.label}
                  </MenuItem>
                )
            )}
          </Select>
        </FormControl>

        {/* Date Filter */}
        <FormControl>
          <SimpleDatePicker
            label={String(i18next.t('filter-date'))}
            value={filters.date || null}
            onChange={(date) => handleFilterChange('date', date)}
          />
        </FormControl>

        {/* Premium/Classic Filter */}
        <FormControl>
          <InputLabel shrink>{i18next.t('filter-customer-type')}</InputLabel>
          <Select
            label={String(i18next.t('filter-customer-type'))}
            value={filters.premium === undefined ? '' : filters.premium ? 'premium' : 'classic'}
            onChange={(e) =>
              handleFilterChange('premium', e.target.value === 'premium' ? true : e.target.value === 'classic' ? false : undefined)
            }
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            <MenuItem value="classic">{i18next.t('filter-customer-type-classic')}</MenuItem>
            <MenuItem value="premium">{i18next.t('filter-customer-type-premium')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 4 }}>
        <Button variant="contained" color="primary" onClick={handleGenerateReport} disabled={loading || !filters.date}>
          {loading ? <CircularProgress size={20} /> : i18next.t('filter-generate-report')}
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          {i18next.t('filter-reset')}
        </Button>
      </Box>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{i18next.t('report-loading')}</Typography>
        </Box>
      )}

      {/* Results Table */}
      {!loading && results && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {i18next.t('earlybird-report-title')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{i18next.t('filter-country')}:</strong> {countryLabel}
            </Typography>
            <Typography variant="body2">
              <strong>{i18next.t('earlybird-date-day')}:</strong>{' '}
              <FormatDateRange from={results.rangeDayFrom} to={results.rangeDayTo} />
            </Typography>
            <Typography variant="body2">
              <strong>{i18next.t('earlybird-date-week')}:</strong>{' '}
              <FormatDateRange from={results.rangeWeekFrom} to={results.rangeWeekTo} />
            </Typography>
            <Typography variant="body2">
              <strong>{i18next.t('earlybird-date-month')}:</strong>{' '}
              <FormatDateRange from={results.rangeMonthFrom} to={results.rangeMonthTo} />
            </Typography>
            <Typography variant="body2">
              <strong>{i18next.t('earlybird-date-year')}:</strong>{' '}
              <FormatDateRange from={results.rangeYearFrom} to={results.rangeYearTo} />
            </Typography>
            <Typography variant="body2">
              <strong>{i18next.t('filter-customer-type')}:</strong> {customerTypeLabel}
            </Typography>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{i18next.t('earlybird-metric')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {i18next.t('earlybird-period-day')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {i18next.t('earlybird-period-week')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {i18next.t('earlybird-period-month')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {i18next.t('earlybird-period-year')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row) => {
                  const handleNumberClick = (period: 'day' | 'week' | 'month' | 'year', value: number) => {
                    if (value === 0 || !results || !appliedFilters) return;

                    // Determine column type based on row label
                    // For Early Bird, we use totalRequests for both Nettoanfragen and Bruttoanfragen
                    // since the modal will show all bookings in that period
                    let columnType: 'totalRequests' | 'approvedCount' | 'rejectedByHotelCount' | 'rejectedByClientCount' | 'totalNights' | 'totalGuests' | null = null;

                    const netLabel = String(i18next.t('contact-net-inquiries'));
                    const grossLabel = String(i18next.t('contact-gross-inquiries'));
                    const reservationsLabel = String(i18next.t('contact-reservations'));
                    const isReservationsRow = row.label === reservationsLabel;

                    if (row.label === netLabel || row.label === grossLabel) {
                      columnType = 'totalRequests';
                    } else if (isReservationsRow) {
                      columnType = 'approvedCount';
                    }

                    // For other rows (Kommission, Hotelumsatz, Abschlussquote), don't make clickable
                    if (!columnType) return;

                    let dateFrom: Date;
                    let dateTo: Date;

                    switch (period) {
                      case 'day':
                        dateFrom = new Date(results.rangeDayFrom);
                        dateTo = new Date(results.rangeDayTo);
                        break;
                      case 'week':
                        dateFrom = new Date(results.rangeWeekFrom);
                        dateTo = new Date(results.rangeWeekTo);
                        break;
                      case 'month':
                        dateFrom = new Date(results.rangeMonthFrom);
                        dateTo = new Date(results.rangeMonthTo);
                        break;
                      case 'year':
                        dateFrom = new Date(results.rangeYearFrom);
                        dateTo = new Date(results.rangeYearTo);
                        break;
                    }

                    const baseFilters: any = {
                      requestDateFrom: dateFrom,
                      requestDateTo: dateTo,
                      ...(appliedFilters.country ? { hotelCountry: appliedFilters.country } : {}),
                      ...(appliedFilters.premium !== undefined ? { clientIsPremium: appliedFilters.premium } : {}),
                    };

                    const isBookingModal = row.label !== grossLabel;
                    setModalEntityType(isBookingModal ? 'booking' : 'offer');
                    setModalFilters({
                      baseFilters,
                      columnType,
                      requestYear: 0,
                      requestMonth: 0,
                      hotelId: 0,
                      offerSource: EOfferSource.LISTER,
                      filterByBookingDate: row.label === netLabel,
                      filterByReservationDate: isReservationsRow,
                    } as StatisticsReportBookingsFiltersInput);
                    const periodLabel =
                      period === 'day'
                        ? i18next.t('earlybird-period-day')
                        : period === 'week'
                        ? i18next.t('earlybird-period-week')
                        : period === 'month'
                        ? i18next.t('earlybird-period-month')
                        : i18next.t('earlybird-period-year');
                    setModalColumnLabel(`${row.label} - ${periodLabel}`);
                    setModalOpen(true);
                  };

                  // Determine if this row should have clickable numbers
                  const netLabel = String(i18next.t('contact-net-inquiries'));
                  const grossLabel = String(i18next.t('contact-gross-inquiries'));
                  const reservationsLabel = String(i18next.t('contact-reservations'));
                  const isClickable = row.label === netLabel || row.label === grossLabel || row.label === reservationsLabel;

                  return (
                    <TableRow key={row.label}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{row.label}</TableCell>
                      <TableCell align="right">
                        {isClickable ? (
                          <Box
                            component="span"
                            onClick={() => handleNumberClick('day', row.day)}
                            sx={{
                              cursor: row.day > 0 ? 'pointer' : 'default',
                              textDecoration: row.day > 0 ? 'underline' : 'none',
                              color: row.day > 0 ? '#AC2A6E' : 'inherit',
                              userSelect: 'none'
                            }}
                          >
                            {row.isCurrency ? formatPrice(row.day) : row.isPercentage ? formatPercentage(row.day / 100, 2) : formatNumber(row.day)}
                          </Box>
                        ) : (
                          row.isCurrency ? formatPrice(row.day) : row.isPercentage ? formatPercentage(row.day / 100, 2) : formatNumber(row.day)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isClickable ? (
                          <Box
                            component="span"
                            onClick={() => handleNumberClick('week', row.week)}
                            sx={{
                              cursor: row.week > 0 ? 'pointer' : 'default',
                              textDecoration: row.week > 0 ? 'underline' : 'none',
                              color: row.week > 0 ? '#AC2A6E' : 'inherit',
                              userSelect: 'none'
                            }}
                          >
                            {row.isCurrency ? formatPrice(row.week) : row.isPercentage ? formatPercentage(row.week / 100, 2) : formatNumber(row.week)}
                          </Box>
                        ) : (
                          row.isCurrency ? formatPrice(row.week) : row.isPercentage ? formatPercentage(row.week / 100, 2) : formatNumber(row.week)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isClickable ? (
                          <Box
                            component="span"
                            onClick={() => handleNumberClick('month', row.month)}
                            sx={{
                              cursor: row.month > 0 ? 'pointer' : 'default',
                              textDecoration: row.month > 0 ? 'underline' : 'none',
                              color: row.month > 0 ? '#AC2A6E' : 'inherit',
                              userSelect: 'none'
                            }}
                          >
                            {row.isCurrency ? formatPrice(row.month) : row.isPercentage ? formatPercentage(row.month / 100, 2) : formatNumber(row.month)}
                          </Box>
                        ) : (
                          row.isCurrency ? formatPrice(row.month) : row.isPercentage ? formatPercentage(row.month / 100, 2) : formatNumber(row.month)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isClickable ? (
                          <Box
                            component="span"
                            onClick={() => handleNumberClick('year', row.year)}
                            sx={{
                              cursor: row.year > 0 ? 'pointer' : 'default',
                              textDecoration: row.year > 0 ? 'underline' : 'none',
                              color: row.year > 0 ? '#AC2A6E' : 'inherit',
                              userSelect: 'none'
                            }}
                          >
                            {row.isCurrency ? formatPrice(row.year) : row.isPercentage ? formatPercentage(row.year / 100, 2) : formatNumber(row.year)}
                          </Box>
                        ) : (
                          row.isCurrency ? formatPrice(row.year) : row.isPercentage ? formatPercentage(row.year / 100, 2) : formatNumber(row.year)
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Export Button */}
      {!loading && results && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExportCSV} color="secondary" size="large">
            {i18next.t('download-csv-report')}
          </Button>
        </Box>
      )}

      {/* Report detail modal: offers for Bruttoanfragen, bookings for Nettoanfragen/Reservierungen */}
      {modalFilters && (
        <StatisticsBookingsModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalFilters(null);
          }}
          filters={modalFilters}
          columnLabel={modalColumnLabel}
          entityType={modalEntityType}
        />
      )}
    </Box>
  );
}
